import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import DOMPurify from "dompurify";
import {
  Plus, Trash2, Eye, EyeOff, GripVertical, Loader2, Edit3,
  Save, X, ArrowUp, ArrowDown,
} from "lucide-react";

import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const PAGE_LABELS = {
  coming_soon: "Coming Soon page",
  prelaunch: "Beta prelaunch landing",
  beta_launch: "Beta launch page (post invite code)",
  launch: "Public launch page",
  dashboard: "Member dashboard",
  global: "Global (every page)",
};

const PLACEMENT_LABELS = {
  top: "Top of page",
  middle: "Middle / body",
  bottom: "Bottom of page",
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "link",
];

// ---------------------------------------------------------------------------
// Editor dialog (rich text WYSIWYG)
// ---------------------------------------------------------------------------

const BlockEditorDialog = ({ initial, onSave, onClose, defaultPage }) => {
  const [draft, setDraft] = useState(() => initial ? { ...initial } : {
    page_key: defaultPage || "beta_launch",
    placement: "bottom",
    content_html: "",
    is_visible: true,
    label: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!draft.content_html || draft.content_html === "<p><br></p>" || draft.content_html === "<p></p>") {
      toast.error("Add some content first.");
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="block-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit text block" : "Add new text block"}</DialogTitle>
          <DialogDescription>
            Format with bold, italic, headings, bullet points, and links. Changes go live immediately after saving — no redeploy.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!initial && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Page</Label>
                <Select value={draft.page_key} onValueChange={(v) => setDraft({ ...draft, page_key: v })}>
                  <SelectTrigger data-testid="block-page-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Placement on page</Label>
                <Select value={draft.placement} onValueChange={(v) => setDraft({ ...draft, placement: v })}>
                  <SelectTrigger data-testid="block-placement-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLACEMENT_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {initial && (
            <div>
              <Label>Placement on page</Label>
              <Select value={draft.placement} onValueChange={(v) => setDraft({ ...draft, placement: v })}>
                <SelectTrigger data-testid="block-placement-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PLACEMENT_LABELS).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Internal nickname (optional, admin-only)</Label>
            <Input
              value={draft.label || ""}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="e.g. 'Founder note about pricing'"
              maxLength={120}
              data-testid="block-label-input"
            />
          </div>
          <div>
            <Label>Content</Label>
            <div className="mt-1 rounded-lg border border-[#E5DFEC] overflow-hidden bg-white" data-testid="block-content-editor">
              <ReactQuill
                theme="snow"
                value={draft.content_html}
                onChange={(html) => setDraft({ ...draft, content_html: html })}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Start typing… use the toolbar for headings, lists, bold, italic, links."
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#6B4E8A] cursor-pointer">
            <Switch
              checked={draft.is_visible}
              onCheckedChange={(v) => setDraft({ ...draft, is_visible: v })}
              data-testid="block-visible-switch"
            />
            Visible on the live page
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white" data-testid="save-block-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-1.5" />}
            {initial ? "Save changes" : "Add block"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main manager — page picker + reorderable list
// ---------------------------------------------------------------------------

const PageBlocksManager = () => {
  const [pageKey, setPageKey] = useState("beta_launch");
  const [allBlocks, setAllBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  // Local reorder state — applied to backend on drop
  const [orderedBlocks, setOrderedBlocks] = useState([]);
  const dragTimer = useRef(null);

  const loadBlocks = async (key = pageKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/founder/page-blocks?page_key=${key}`);
      setAllBlocks(res.data.blocks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load blocks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBlocks(pageKey); }, [pageKey]);
  useEffect(() => { setOrderedBlocks(allBlocks); }, [allBlocks]);

  const grouped = useMemo(() => {
    const out = { top: [], middle: [], bottom: [] };
    orderedBlocks.forEach((b) => {
      const slot = out[b.placement] ? b.placement : "bottom";
      out[slot].push(b);
    });
    return out;
  }, [orderedBlocks]);

  const persistReorder = (placement, newList) => {
    // Merge the reordered slice back into the full list, then send to backend.
    const others = orderedBlocks.filter((b) => b.placement !== placement);
    const merged = [...others, ...newList];
    setOrderedBlocks(merged);
    // Debounce the API call so a fast drag doesn't pummel the backend
    clearTimeout(dragTimer.current);
    dragTimer.current = setTimeout(async () => {
      try {
        // Send the ordered ids for this placement only — backend handles
        // sort_order globally per page, so include every block from the page.
        const orderedIds = ["top", "middle", "bottom"].flatMap((p) =>
          (p === placement ? newList : others.filter((b) => b.placement === p))
            .map((b) => b.block_id)
        );
        await axios.post(`${API}/founder/page-blocks/reorder`, {
          page_key: pageKey,
          block_ids: orderedIds,
        });
        toast.success("Order saved.", { duration: 1200 });
        loadBlocks();
      } catch (err) {
        toast.error("Couldn't save new order.");
        loadBlocks();
      }
    }, 400);
  };

  const createBlock = async (payload) => {
    try {
      await axios.post(`${API}/founder/page-blocks`, payload);
      toast.success("Block added — live now on " + PAGE_LABELS[payload.page_key]);
      setPageKey(payload.page_key);
      loadBlocks(payload.page_key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create block.");
      throw err;
    }
  };

  const updateBlock = async (payload) => {
    try {
      const { block_id, ...patch } = payload;
      await axios.put(`${API}/founder/page-blocks/${block_id || editing.block_id}`, patch);
      toast.success("Block updated.");
      loadBlocks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update block.");
      throw err;
    }
  };

  const deleteBlock = async (block) => {
    if (!window.confirm("Delete this block? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/founder/page-blocks/${block.block_id}`);
      toast.success("Block deleted.");
      loadBlocks();
    } catch (err) {
      toast.error("Couldn't delete block.");
    }
  };

  const toggleVisibility = async (block) => {
    try {
      await axios.put(`${API}/founder/page-blocks/${block.block_id}`, { is_visible: !block.is_visible });
      loadBlocks();
    } catch (err) {
      toast.error("Couldn't update visibility.");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Custom text blocks
          </h3>
          <p className="text-sm text-[#64748B] mt-0.5">
            Add formatted text anywhere on the platform. Pick a page, drag to reorder, changes go live instantly.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setShowEditor(true); }}
          className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"
          data-testid="add-page-block-btn"
        >
          <Plus size={14} className="mr-1.5" /> Add text block
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">Page:</Label>
        <Select value={pageKey} onValueChange={setPageKey}>
          <SelectTrigger className="w-72" data-testid="block-manager-page-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(PAGE_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#94A3B8] ml-auto">{orderedBlocks.length} block{orderedBlocks.length === 1 ? "" : "s"} on this page</span>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" /></div>
      ) : orderedBlocks.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#94A3B8] border border-dashed border-[#E5DFEC] rounded-xl">
          No custom blocks on this page yet. Click <strong>Add text block</strong> to create the first one.
        </div>
      ) : (
        <div className="space-y-6">
          {["top", "middle", "bottom"].map((slot) => (
            <div key={slot} className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold">
                  {PLACEMENT_LABELS[slot]} ({grouped[slot].length})
                </span>
                <div className="flex-1 h-px bg-[#E5DFEC]" />
              </div>
              {grouped[slot].length === 0 ? (
                <p className="text-xs text-[#94A3B8] italic pl-4">No blocks here yet.</p>
              ) : (
                <Reorder.Group
                  axis="y"
                  values={grouped[slot]}
                  onReorder={(next) => persistReorder(slot, next)}
                  className="space-y-2"
                >
                  {grouped[slot].map((b) => (
                    <Reorder.Item
                      key={b.block_id}
                      value={b}
                      className="bg-white border border-[#E5DFEC] rounded-xl p-4 cursor-grab active:cursor-grabbing"
                      data-testid={`block-row-${b.block_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <GripVertical size={16} className="text-[#94A3B8] mt-1 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {b.label && <span className="font-medium text-sm text-[#6B4E8A]">{b.label}</span>}
                            {!b.is_visible && (
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C]">
                                Hidden
                              </span>
                            )}
                          </div>
                          <div
                            className="prose prose-sm max-w-none mt-1 text-[#475569] line-clamp-3"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(b.content_html || ""),
                            }}
                          />
                          <p className="text-[10px] text-[#94A3B8] mt-1">Updated {new Date(b.updated_at).toLocaleDateString("en-CA")}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => toggleVisibility(b)} title={b.is_visible ? "Hide" : "Show"} data-testid={`toggle-visibility-${b.block_id}`}>
                            {b.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(b); setShowEditor(true); }} data-testid={`edit-block-${b.block_id}`}>
                            <Edit3 size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteBlock(b)} className="text-[#DC2626]" data-testid={`delete-block-${b.block_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              )}
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <BlockEditorDialog
          initial={editing}
          defaultPage={pageKey}
          onClose={() => { setShowEditor(false); setEditing(null); }}
          onSave={(payload) => editing
            ? updateBlock({ ...payload, block_id: editing.block_id })
            : createBlock(payload)
          }
        />
      )}
    </div>
  );
};

export default PageBlocksManager;
